import { PhoneOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, InputNumber, Space, Switch, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { type SipGlobalConfig, buildWsUri } from '../../api/sip-config.js';

interface Props {
  cfg: SipGlobalConfig;
  form: FormInstance<SipGlobalConfig>;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
}

export function SipGlobalConfigCard({ cfg, form, saving, onSave, onReset }: Props) {
  return (
    <Card title="全局参数" style={{ marginBottom: 16 }}>
      <Form<SipGlobalConfig> form={form} layout="vertical" initialValues={cfg} autoComplete="off">
        <Form.Item name="enabled" label="启用通话网关" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          name="server"
          label="SIP 服务器"
          rules={[{ required: true, message: '请输入服务器地址' }]}
        >
          <Input placeholder="crest.baicells.com" />
        </Form.Item>

        <Space style={{ display: 'flex' }} size={16}>
          <Form.Item
            name="port"
            label="端口"
            rules={[{ required: true, message: '请输入端口' }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={1} max={65535} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="wsPath" label="WebSocket 路径" style={{ flex: 1 }}>
            <Input placeholder="/sip-ws" />
          </Form.Item>
          <Form.Item name="secure" label="启用 WSS" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item
          name="password"
          label="SIP 注册密码"
          tooltip="由超管统一配置，所有开通通话功能的用户默认使用该密码注册"
          rules={[{ required: true, message: '请输入注册密码' }]}
        >
          <Input.Password placeholder="由配置注入" />
        </Form.Item>

        <Form.Item name="realm" label="SIP Realm">
          <Input placeholder="113.195.88.185" />
        </Form.Item>

        <Space style={{ display: 'flex' }} size={16} align="start">
          <Form.Item
            name="extensionStart"
            label="号码范围起始"
            rules={[
              { required: true, message: '请输入号码范围起始值' },
              { pattern: /^\d+$/, message: '号码只能包含数字' },
            ]}
            style={{ flex: 1 }}
          >
            <Input placeholder="89660031" />
          </Form.Item>
          <Form.Item
            name="extensionEnd"
            label="号码范围结束"
            dependencies={['extensionStart']}
            rules={[
              { required: true, message: '请输入号码范围结束值' },
              { pattern: /^\d+$/, message: '号码只能包含数字' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = String(getFieldValue('extensionStart') ?? '').trim();
                  const end = String(value ?? '').trim();
                  if (!start || !end || !/^\d+$/.test(start) || !/^\d+$/.test(end)) {
                    return Promise.resolve();
                  }
                  return Number(end) >= Number(start)
                    ? Promise.resolve()
                    : Promise.reject(new Error('结束值必须大于或等于起始值'));
                },
              }),
            ]}
            style={{ flex: 1 }}
          >
            <Input placeholder="89660099" />
          </Form.Item>
        </Space>

        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
          WebSocket URI: <Typography.Text code>{buildWsUri(cfg)}</Typography.Text>
        </Typography.Paragraph>

        <Space>
          <Button type="primary" loading={saving} onClick={onSave} icon={<PhoneOutlined />}>
            保存参数
          </Button>
          <Button icon={<ReloadOutlined />} onClick={onReset}>
            重置
          </Button>
        </Space>
      </Form>
    </Card>
  );
}
